import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Row, Col, FormGroup, Label, Input, Button, Spinner } from 'reactstrap';
import Select from 'components/Select';
import StatesInput from 'components/StatesInput';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const ChannelAddForm = memo(({
    formData,
    activeColor,
    isTheme2 = false,
    handleInputChange,
    handleSingleSelectChange,
    mapValueToOption,
    errors,
    statusOptions,
    countryOptions,
    typeOptions,
    isStateRequired,
    createChannelMutation,
    updateChannelMutation,
    updateSalesLocationMutation,
    back,
    isEditMode,
    handleCancel,
    activeColorStyle: activeColorStyleProp,
    t
}) => {
    const activeColorStyle = useMemo(
        () => activeColorStyleProp ?? getActiveColorStyle(activeColor),
        [activeColorStyleProp, activeColor]
    );
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
    const inputClass = (...extra) => classNames('form-control', ...extra.filter(Boolean));
    const pending =
        createChannelMutation.isPending ||
        updateChannelMutation.isPending;

    return (
        <>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for="type" className="form-control-label">
                            {t("channelsAdd.type")}
                        </Label>
                    <Select
                        name="type"
                        className="react-select"
                        styles={selectStyles}
                        isSearchable
                        activeColor={activeColor}
                        closeMenuOnSelect
                        options={typeOptions}
                        value={mapValueToOption(formData.type, typeOptions)}
                        onChange={handleSingleSelectChange("type")}
                        isDisabled={pending || back}
                    />
                </FormGroup>
            </Col>
            <Col md="4">
                <FormGroup>
                    <Label for="name" className="form-control-label">
                        {t("channelsAdd.name")}{" "}
                        <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={inputClass(errors.name && "is-invalid")}
                        style={inputStyle}
                        placeholder={t("channelsAdd.enterChannelName")}
                        disabled={pending}
                    />
                    {errors.name && (
                        <div className="invalid-feedback d-block">
                            {errors.name}
                        </div>
                    )}
                </FormGroup>
            </Col>
            <Col md="4">
                <FormGroup>
                    <Label for="description" className="form-control-label">
                        {t("channelsAdd.description")}
                    </Label>
                    <Input
                        id="description"
                        name="description"
                        type="text"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={errors.description ? "is-invalid" : ""}
                        placeholder={t("channelsAdd.enterChannelDescription")}
                        disabled={
                            createChannelMutation.isPending ||
                            updateChannelMutation.isPending
                        }
                    />
                    {errors.description && (
                        <div className="invalid-feedback d-block">
                            {errors.description}
                        </div>
                    )}
                </FormGroup>
            </Col>
            </Row>

            {isEditMode && (
                <Row>
                    <Col md="3">
                    <FormGroup>
                        <Label for="status" className="form-control-label">
                            {t("channelsAdd.status")}
                        </Label>
                        <Select
                            inputId="status"
                            name="status"
                            className="react-select"
                            styles={selectStyles}
                            isSearchable
                            closeMenuOnSelect
                            activeColor={activeColor}
                            options={statusOptions}
                            value={mapValueToOption(
                                formData.status,
                                statusOptions
                            )}
                            onChange={handleSingleSelectChange("status")}
                            isDisabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                </Row>
            )}

            {/* FED ID and Legal Name */}
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for="fed_id" className="form-control-label">
                            {t("channelsAdd.fedId")}
                        </Label>
                        <Input
                            id="fed_id"
                            name="fed_id"
                            type="text"
                            value={formData.fed_id}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterFedId")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for="legal_name" className="form-control-label">
                            {t("channelsAdd.legalName")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                            id="legal_name"
                            name="legal_name"
                            type="text"
                            value={formData.legal_name}
                            onChange={handleInputChange}
                            className={errors.legal_name ? "is-invalid" : ""}
                            placeholder={t("channelsAdd.enterLegalName")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                        {errors.legal_name && (
                            <div className="invalid-feedback d-block">
                                {errors.legal_name}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for="address_1" className="form-control-label">
                            {t("channelsAdd.address1")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                            id="address_1"
                            name="address_1"
                            type="text"
                            value={formData.address_1}
                            onChange={handleInputChange}
                            className={errors.address_1 ? "is-invalid" : ""}
                            placeholder={t("channelsAdd.enterAddressLine1")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                        {errors.address_1 && (
                            <div className="invalid-feedback d-block">
                                {errors.address_1}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for="address_2" className="form-control-label">
                            {t("channelsAdd.address2")}
                        </Label>
                        <Input
                            id="address_2"
                            name="address_2"
                            type="text"
                            value={formData.address_2}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterAddressLine2")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for="zip" className="form-control-label">
                            {t("channelsAdd.zip")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                            id="zip"
                            name="zip"
                            type="text"
                            value={formData.zip}
                            onChange={handleInputChange}
                            className={errors.zip ? "is-invalid" : ""}
                            placeholder={t("channelsAdd.enterZipCode")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                        {errors.zip && (
                            <div className="invalid-feedback d-block">
                                {errors.zip}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="city" className="form-control-label">
                            {t("channelsAdd.city")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                            id="city"
                            name="city"
                            type="text"
                            value={formData.city}
                            onChange={handleInputChange}
                            className={errors.city ? "is-invalid" : ""}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        ></Input>
                        {errors.city && (
                            <div className="invalid-feedback d-block">
                                {errors.city}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="state" className="form-control-label">
                            {t("channelsAdd.state")}{" "}
                            {isStateRequired && (
                                <span style={{ color: "red" }}>*</span>
                            )}
                        </Label>
                        <StatesInput
                            inputId="state"
                            name="state"
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            isSearchable
                            placeholder={
                                !formData.country
                                    ? "Select country first"
                                    : !isStateRequired
                                      ? "No states for selected country"
                                      : "Select state"
                            }
                            value={
                                typeof formData.state === "object" && formData.state?.id
                                    ? formData.state.id
                                    : formData.state || null
                            }
                            onChange={(stateObject) => {
                                handleInputChange({
                                    target: {
                                        name: "state",
                                        value: stateObject
                                            ? {
                                                  id: stateObject.id,
                                                  abbreviation: stateObject.abbreviation,
                                              }
                                            : "",
                                    },
                                });
                            }}
                            isDisabled={pending || !formData.country}
                            countrySelected={!!formData.country}
                            isClearable
                        />
                        {errors.state && (
                            <div className="invalid-feedback d-block">
                                {errors.state}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for="county" className="form-control-label">
                            {t("channelsAdd.county")}
                        </Label>
                        <Input
                            id="county"
                            name="county"
                            type="text"
                            value={formData.county}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterCounty")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label for="country" className="form-control-label">
                            {t("channelsAdd.country")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            placeholder="Select country"
                            name="country"
                            isSearchable
                            closeMenuOnSelect
                            value={mapValueToOption(formData.country, countryOptions)}
                            onChange={handleSingleSelectChange("country")}
                            options={countryOptions}
                            isDisabled={pending}
                            isClearable
                        />
                        {errors.country && (
                            <div className="invalid-feedback d-block">
                                {errors.country}
                            </div>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <h5 className="mt-2">Contact Information</h5>
            {/* Contact Information */}
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label
                            for="contact_person"
                            className="form-control-label"
                        >
                            {t("channelsAdd.contactPerson")}
                        </Label>
                        <Input
                            id="contact_person"
                            name="contact_person"
                            type="text"
                            value={formData.contact_person}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterContactPersonName")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="phone_1" className="form-control-label">
                            {t("channelsAdd.phone1")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                            id="phone_1"
                            name="phone_1"
                            type="text"
                            value={formData.phone_1}
                            onChange={handleInputChange}
                            className={errors.phone_1 ? "is-invalid" : ""}
                            placeholder={t("channelsAdd.enterPhoneNumber")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                        {errors.phone_1 && (
                            <div className="invalid-feedback d-block">
                                {errors.phone_1}
                            </div>
                        )}
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="phone_2" className="form-control-label">
                            {t("channelsAdd.phone2")}
                        </Label>
                        <Input
                            id="phone_2"
                            name="phone_2"
                            type="text"
                            value={formData.phone_2}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterPhoneNumber")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label for="fax_1" className="form-control-label">
                            {t("channelsAdd.fax1")}
                        </Label>
                        <Input
                            id="fax_1"
                            name="fax_1"
                            type="text"
                            value={formData.fax_1}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterFaxNumber")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="fax_2" className="form-control-label">
                            {t("channelsAdd.fax2")}
                        </Label>
                        <Input
                            id="fax_2"
                            name="fax_2"
                            type="text"
                            value={formData.fax_2}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterFaxNumber")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label for="website" className="form-control-label">
                            {t("channelsAdd.website")}
                        </Label>
                        <Input
                            id="website"
                            name="website"
                            type="url"
                            value={formData.website}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterWebsiteUrl")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for="email" className="form-control-label">
                            {t("channelsAdd.email")}
                        </Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterEmailAddress")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <h5 className="mt-2">Additional Information</h5>
            {/* Additional Information */}
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label for="text_name" className="form-control-label">
                            {t("channelsAdd.textName")}{" "}
                            <small>(max 12 characters)</small>
                        </Label>
                        <Input
                            id="text_name"
                            name="text_name"
                            type="text"
                            value={formData.text_name}
                            onChange={handleInputChange}
                            maxLength={12}
                            placeholder={t("channelsAdd.enterTextName")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label
                            for="accounting_class"
                            className="form-control-label"
                        >
                            {t("channelsAdd.accountingClass")}
                        </Label>
                        <Input
                            id="accounting_class"
                            name="accounting_class"
                            type="text"
                            value={formData.accounting_class}
                            onChange={handleInputChange}
                            placeholder={t("channelsAdd.enterAccountingClass")}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            {formData.type === "sales_group_person" && (
                <>
                    <Row>
                        <Col md="6">
                            <div className="">
                                <Row className="align-items-center">
                                    <Col md="4">
                                        <FormGroup
                                            check
                                            className="mb-0 d-flex align-items-center"
                                        >
                                            <Label
                                                check
                                                className="mb-0 d-flex align-items-center"
                                            >
                                                <Input
                                                    id="sales_role"
                                                    name="sales_role"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!formData.sales_role}
                                                    onChange={handleInputChange}
                                                    disabled={
                                                        createChannelMutation.isPending ||
                                                        updateChannelMutation.isPending
                                                    }
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>
                                                    Sales Role
                                                </span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup
                                            check
                                            className="mb-0 d-flex align-items-center"
                                        >
                                            <Label
                                                check
                                                className="mb-0 d-flex align-items-center"
                                            >
                                                <Input
                                                    id="fi_role"
                                                    name="fi_role"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!formData.fi_role}
                                                    onChange={handleInputChange}
                                                    disabled={
                                                        createChannelMutation.isPending ||
                                                        updateChannelMutation.isPending
                                                    }
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>
                                                    FI Role
                                                </span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup
                                            check
                                            className="mb-0 d-flex align-items-center"
                                        >
                                            <Label
                                                check
                                                className="mb-0 d-flex align-items-center"
                                            >
                                                <Input
                                                    id="sales_manager"
                                                    name="sales_manager"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!formData.sales_manager}
                                                    onChange={handleInputChange}
                                                    disabled={
                                                        createChannelMutation.isPending ||
                                                        updateChannelMutation.isPending
                                                    }
                                                />
                                                <span className="form-check-sign">
                                                    <span className="check" />
                                                </span>
                                                <span style={{ marginLeft: 8 }}>
                                                    Manager Role
                                                </span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12" className="mt-2">
                                        <FormGroup>
                                            <Label className="form-control-label">
                                                Employee Code:
                                            </Label>
                                            <Input
                                                id="employee_code"
                                                name="employee_code"
                                                type="text"
                                                value={formData.employee_code}
                                                onChange={handleInputChange}
                                                placeholder={
                                                    t("channelsAdd.enterEmployeeCode") ||
                                                    "Enter Employee Code"
                                                }
                                                disabled={
                                                    createChannelMutation.isPending ||
                                                    updateChannelMutation.isPending
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
            <Row>
                <Col md="12">
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button
                            color="secondary"
                            onClick={handleCancel}
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending
                            }
                            className="px-4"
                        >
                            {t("channelsAdd.cancel")}
                        </Button>
                        <Button
                            style={{ ...activeColorStyle, color: "white" }}
                            type="submit"
                            disabled={
                                createChannelMutation.isPending ||
                                updateChannelMutation.isPending ||
                                updateSalesLocationMutation.isPending
                            }
                            className="px-4"
                        >
                            {createChannelMutation.isPending ||
                            updateChannelMutation.isPending ||
                            updateSalesLocationMutation.isPending ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    {t("channelsAdd.saving")}
                                </>
                            ) : (
                                <>
                                    <i
                                        className={
                                            isEditMode
                                                ? "tim-icons icon-pencil"
                                                : "tim-icons icon-simple-add"
                                        }
                                        style={{ marginRight: "8px" }}
                                    />
                                    {t("channelsAdd.save")}
                                </>
                            )}
                        </Button>
                    </div>
                </Col>
            </Row>
        </>
    );
});

ChannelAddForm.displayName = "ChannelAddForm";

export default ChannelAddForm;
